<template>
  <div class="auth-wrapper">
    <div class="auth-container">
      <div class="auth-header">
        <h1 class="auth-title">LOCKLINE+</h1>
        <p class="auth-subtitle">請使用客戶帳號登入</p>
      </div>

      <div v-if="prefilled" class="prefill-notice">
        帳號資訊已自動填入，請輸入您設定的密碼繼續。
      </div>

      <div class="auth-form">
        <form @submit.prevent="handleLogin">
          <div class="form-group">
            <label for="clientCode">客戶代碼</label>
            <input
              id="clientCode"
              v-model="form.clientCode"
              type="text"
              class="form-control"
              placeholder="請輸入客戶代碼（不分大小寫）"
              required
            />
          </div>

          <div class="form-group">
            <label for="username">登入帳號</label>
            <input
              id="username"
              v-model="form.username"
              type="text"
              class="form-control"
              placeholder="請輸入登入帳號"
              required
              autocomplete="username"
            />
          </div>

          <div class="form-group">
            <label for="password">密碼</label>
            <input
              id="password"
              ref="passwordInput"
              v-model="form.password"
              type="password"
              class="form-control"
              placeholder="請輸入密碼"
              required
              autocomplete="current-password"
            />
          </div>

          <div v-if="errorMessage" class="alert alert-danger">
            {{ errorMessage }}
          </div>

          <button
            type="submit"
            class="btn btn-primary btn-block"
            :disabled="loading"
          >
            <span v-if="loading">登入中...</span>
            <span v-else>登入</span>
          </button>
        </form>
      </div>

      <div class="auth-footer">
        <p class="help-text">
          如有帳號問題，請聯絡您的平台管理員
        </p>
        <p class="switch-links">
          <router-link to="/platform/login">平台管理登入</router-link>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import authService from '@/helpers/authservice/authService.js';

export default {
  name: 'ClientLogin',
  setup() {
    const router = useRouter();
    const route = useRoute();
    const loading = ref(false);
    const errorMessage = ref('');
    const prefilled = ref(false);
    const passwordInput = ref(null);

    const form = reactive({
      clientCode: '',
      username: '',
      password: '',
    });

    onMounted(async () => {
      const { clientCode, email } = route.query;
      if (clientCode) {
        form.clientCode = String(clientCode);
        prefilled.value = true;
      }
      if (email) {
        form.username = String(email);
        prefilled.value = true;
      }
      if (prefilled.value) {
        await nextTick();
        passwordInput.value?.focus();
      }
    });

    const handleLogin = async () => {
      loading.value = true;
      errorMessage.value = '';

      try {
        console.log('[ClientLogin] 開始登入:', form.clientCode, form.username);
        
        const result = await authService.clientLogin(
          form.clientCode,
          form.username,
          form.password
        );

        console.log('[ClientLogin] 登入結果:', result);

        if (result.success) {
          console.log('[ClientLogin] 登入成功，準備跳轉');
          console.log('[ClientLogin] 帳號資訊:', result.account);
          console.log('[ClientLogin] Token:', result.token ? '已取得' : '未取得');
          
          // 檢查 localStorage
          console.log('[ClientLogin] localStorage auth_account_type:', localStorage.getItem('auth_account_type'));
          console.log('[ClientLogin] localStorage auth_user:', localStorage.getItem('auth_user'));
          
          router.push('/client/dashboard');
        } else {
          console.error('[ClientLogin] 登入失敗:', result.error);
          errorMessage.value = result.error || '登入失敗，請檢查帳號密碼';
        }
      } catch (error) {
        console.error('[ClientLogin] 登入異常:', error);
        errorMessage.value = error.message || '登入失敗，請稍後再試';
      } finally {
        loading.value = false;
        console.log('[ClientLogin] 登入流程結束');
      }
    };

    return {
      form,
      loading,
      errorMessage,
      prefilled,
      passwordInput,
      handleLogin,
    };
  },
};
</script>

<style scoped>
.auth-wrapper {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  padding: 20px;
}

.auth-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 420px;
  padding: 40px;
}

.auth-header {
  text-align: center;
  margin-bottom: 30px;
}

.auth-title {
  font-size: 28px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.auth-subtitle {
  color: #666;
  font-size: 14px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: #333;
  font-weight: 500;
  font-size: 14px;
}

.form-control {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.3s;
}

.form-control:focus {
  outline: none;
  border-color: #4facfe;
  box-shadow: 0 0 0 3px rgba(79, 172, 254, 0.1);
}

.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-primary {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(79, 172, 254, 0.4);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-block {
  width: 100%;
}

.alert {
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 14px;
}

.alert-danger {
  background-color: #fee;
  color: #c33;
  border: 1px solid #fcc;
}

.auth-footer {
  margin-top: 24px;
  text-align: center;
  font-size: 14px;
}

.auth-footer .help-text {
  color: #666;
  margin: 0 0 12px 0;
}

.auth-footer .switch-links {
  margin: 0;
}

.auth-footer .switch-links a {
  color: #4facfe;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s;
}

.auth-footer .switch-links a:hover {
  color: #00f2fe;
  text-decoration: underline;
}

.prefill-notice {
  background: #f0fdf4;
  border: 1px solid #86efac;
  color: #166534;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  margin-bottom: 20px;
  text-align: center;
}
</style>
